import React, { useState } from 'react';
import { BellAlertIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { EventReminder, EventReminderInput } from '../../types/event';
import {
  CUSTOM_UNITS,
  CustomUnit,
  MAX_REMINDERS,
  REMINDER_PRESETS,
  calculateScheduledAt,
  describeReminder,
  formatScheduledAt,
  hasReminder,
  reminderKey,
  sortReminders,
  toggleReminder,
} from '../../utils/eventReminders';

interface ReminderPickerProps {
  value: EventReminderInput[];
  onChange: (reminders: EventReminderInput[]) => void;
  /** Az esemény kezdete (ISO). Ha meg van adva, kiírjuk a tényleges küldési időpontokat. */
  eventStart?: string | null;
  /** A már mentett emlékeztetők - ezekből derül ki, melyik ment már ki. */
  savedReminders?: EventReminder[];
  disabled?: boolean;
}

/**
 * Emlékeztető-választó: gyorsgombok (chipek) + egyéni időzítés.
 * Mobilon a chipek tördelve, tapintható méretben jelennek meg.
 */
const ReminderPicker: React.FC<ReminderPickerProps> = ({
  value,
  onChange,
  eventStart,
  savedReminders,
  disabled = false,
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [customAmount, setCustomAmount] = useState('30');
  const [customUnit, setCustomUnit] = useState<CustomUnit>('minutes');
  const [customError, setCustomError] = useState<string | null>(null);

  const limitReached = value.length >= MAX_REMINDERS;

  const handleToggle = (reminder: EventReminderInput) => {
    if (disabled) return;

    if (!hasReminder(value, reminder) && limitReached) {
      return;
    }

    onChange(toggleReminder(value, reminder));
  };

  const handleAddCustom = () => {
    const amount = parseInt(customAmount, 10);
    const unit = CUSTOM_UNITS.find((u) => u.value === customUnit)!;

    if (!Number.isFinite(amount) || amount <= 0) {
      setCustomError('Adj meg egy 0-nál nagyobb számot.');
      return;
    }

    const offsetMinutes = amount * unit.multiplier;
    if (offsetMinutes > 30 * 24 * 60) {
      setCustomError('Legfeljebb 30 nappal az esemény előtt lehet emlékeztetni.');
      return;
    }

    const reminder: EventReminderInput = { offsetMinutes };
    if (hasReminder(value, reminder)) {
      setCustomError('Ez az emlékeztető már be van állítva.');
      return;
    }

    onChange([...value, reminder]);
    setCustomError(null);
    setCustomOpen(false);
  };

  // Ami nincs a gyorsgombok között, azt külön, törölhető chipként mutatjuk
  const presetKeys = new Set(REMINDER_PRESETS.map((p) => reminderKey(p.reminder)));
  const customReminders = value.filter((r) => !presetKeys.has(reminderKey(r)));

  const sentKeys = new Set(
    (savedReminders ?? []).filter((r) => r.sentAt).map((r) => reminderKey(r))
  );

  const preview = eventStart ? sortReminders(value, eventStart) : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {REMINDER_PRESETS.map((preset) => {
          const selected = hasReminder(value, preset.reminder);
          const blocked = !selected && limitReached;

          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => handleToggle(preset.reminder)}
              disabled={disabled || blocked}
              aria-pressed={selected}
              className={`px-3 py-2 text-sm rounded-full border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                selected
                  ? 'bg-primary-600 border-primary-600 text-white hover:bg-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {preset.label}
            </button>
          );
        })}

        {customReminders.map((reminder) => (
          <span
            key={reminderKey(reminder)}
            className="inline-flex items-center gap-1 pl-3 pr-1 py-2 text-sm rounded-full bg-primary-600 border border-primary-600 text-white"
          >
            {describeReminder(reminder)}
            <button
              type="button"
              onClick={() => handleToggle(reminder)}
              disabled={disabled}
              className="p-1 rounded-full hover:bg-primary-700 disabled:opacity-50"
              aria-label={`${describeReminder(reminder)} eltávolítása`}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </span>
        ))}

        {!customOpen && (
          <button
            type="button"
            onClick={() => {
              setCustomError(null);
              setCustomOpen(true);
            }}
            disabled={disabled || limitReached}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-full border border-dashed border-gray-400 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4" />
            Egyéni
          </button>
        )}
      </div>

      {customOpen && (
        <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <input
              type="number"
              min="1"
              inputMode="numeric"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              className="w-full sm:w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Mennyivel az esemény előtt"
            />
            <select
              value={customUnit}
              onChange={(e) => setCustomUnit(e.target.value as CustomUnit)}
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Mértékegység"
            >
              {CUSTOM_UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
            <span className="hidden sm:inline text-sm text-gray-600">előtte</span>

            <div className="flex gap-2 sm:ml-auto">
              <button
                type="button"
                onClick={handleAddCustom}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Hozzáadás
              </button>
              <button
                type="button"
                onClick={() => {
                  setCustomOpen(false);
                  setCustomError(null);
                }}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Mégse
              </button>
            </div>
          </div>

          {customError && <p className="text-sm text-red-600">{customError}</p>}
        </div>
      )}

      {limitReached && (
        <p className="text-xs text-gray-500">
          Legfeljebb {MAX_REMINDERS} emlékeztető állítható be eseményenként.
        </p>
      )}

      {value.length === 0 ? (
        <p className="text-sm text-gray-500">
          Nincs emlékeztető beállítva - válassz a fenti lehetőségekből.
        </p>
      ) : (
        preview.length > 0 && (
          <div className="flex items-start gap-2 text-sm text-gray-600">
            <BellAlertIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              {preview.map((reminder) => {
                const scheduled = calculateScheduledAt(eventStart!, reminder);
                const sent = sentKeys.has(reminderKey(reminder));
                const late = scheduled ? scheduled >= new Date(eventStart!) : false;

                return (
                  <div key={reminderKey(reminder)} className={late ? 'text-amber-700' : undefined}>
                    {scheduled ? formatScheduledAt(scheduled) : describeReminder(reminder)}
                    {sent && <span className="text-gray-400"> · már elment</span>}
                    {late && <span> · az esemény után lenne, nem megy ki</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ReminderPicker;
